import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Table,
  ScrollArea,
  Text,
  Group,
  ActionIcon,
  Menu,
  Tooltip,
  useMantineTheme,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useMediaQuery } from '@mantine/hooks';
import {
  IconDots,
  IconTrash,
  IconEye,
  IconChevronDown,
  IconChevronUp,
  IconCopy,
  IconExternalLink,
} from '@tabler/icons-react';
import { Content } from '@services/content-service';
import { getFlagEmoji } from '@/utils/languageUtils';
import { extractHostname } from '@/utils/urlUtils';
import styles from './ContentTable.module.scss';

interface ContentTableBodyProps {
  contents: Content[];
  handleSort: (column: keyof Content) => void;
  renderSortIcon: (column: keyof Content) => React.ReactNode;
  formatDate: (date: string) => string;
  renderStatus: (status: string) => React.ReactNode;
  handlePublishToggle: (content: Content) => void;
  handleDelete: (content: Content) => void;
}

export const ContentTableBody = ({
  contents,
  handleSort,
  renderSortIcon,
  formatDate,
  renderStatus,
  handlePublishToggle,
  handleDelete,
}: ContentTableBodyProps) => {
  const { t } = useTranslation();
  const theme = useMantineTheme();

  const isMobile = useMediaQuery(`(max-width: ${theme.breakpoints.sm})`);
  const isTablet = useMediaQuery(`(max-width: ${theme.breakpoints.md})`);

  const [expandedRows, setExpandedRows] = useState<Record<number, boolean>>({});

  const visibleColumns = isMobile
    ? ['date', 'content', 'status', 'actions']
    : isTablet
      ? ['date', 'content', 'lang', 'status', 'actions']
      : ['date', 'content', 'source', 'lang', 'status', 'actions'];

  const handleContentClick = (index: number) => {
    setExpandedRows(prev => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  const isTextTruncated = (text: string, maxLines: number) => {
    const textLength = text.length;
    const estimatedLines = Math.ceil(textLength / 50);
    return estimatedLines > maxLines;
  };

  const handleCopyContent = async (content: string, source: string) => {
    try {
      const hashtagRegex = /(#[\w]+)/g;
      const hashtags = content.match(hashtagRegex) || [];
      const formattedContent = content.replace(hashtagRegex, '').trim();
      const textToCopy = [formattedContent, hashtags.length > 0 ? hashtags.join(' ') : '', source]
        .filter(Boolean)
        .join('\n\n');

      await window.navigator.clipboard.writeText(textToCopy);

      notifications.show({
        title: t('common.success'),
        message: t('common.copied'),
        color: 'green',
        autoClose: 2000,
      });
    } catch {
      notifications.show({
        title: t('common.error'),
        message: t('common.copyError'),
        color: 'red',
        autoClose: 3000,
      });
    }
  };

  return (
    <ScrollArea>
      <Table striped highlightOnHover>
        <Table.Thead>
          <Table.Tr>
            {visibleColumns.includes('date') && (
              <Table.Th>
                <Group
                  className={styles['contentTable__header-sort']}
                  onClick={() => handleSort('date')}
                >
                  {t('newContent.table.date')}
                  {renderSortIcon('date')}
                </Group>
              </Table.Th>
            )}
            {visibleColumns.includes('content') && (
              <Table.Th className={styles['contentTable__th--content']}>
                {t('newContent.table.content')}
              </Table.Th>
            )}
            {visibleColumns.includes('source') && (
              <Table.Th>{t('newContent.table.source')}</Table.Th>
            )}
            {visibleColumns.includes('lang') && (
              <Table.Th>
                <Group
                  className={styles['contentTable__header-sort']}
                  onClick={() => handleSort('lang')}
                >
                  {t('newContent.table.lang')}
                  {renderSortIcon('lang')}
                </Group>
              </Table.Th>
            )}
            {visibleColumns.includes('status') && (
              <Table.Th>
                <Group
                  className={styles['contentTable__header-sort']}
                  onClick={() => handleSort('status')}
                >
                  {t('newContent.table.status')}
                  {renderSortIcon('status')}
                </Group>
              </Table.Th>
            )}
            {visibleColumns.includes('actions') && <Table.Th>{t('common.actions')}</Table.Th>}
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {contents.map((content, index) => {
            const maxLines = isMobile ? 1 : isTablet ? 2 : 3;
            const isExpanded = expandedRows[index] || false;
            const isTruncated = isTextTruncated(content.content, maxLines);

            return (
              <Table.Tr
                key={index}
                className={index % 2 === 0 ? styles['contentTable__row--even'] : ''}
              >
                {visibleColumns.includes('date') && <Table.Td>{formatDate(content.date)}</Table.Td>}

                {visibleColumns.includes('content') && (
                  <Table.Td
                    className={`${isTruncated ? styles['contentTable__content--expandable'] : ''}`}
                    onClick={() => isTruncated && handleContentClick(index)}
                  >
                    <Text size="sm" lineClamp={isExpanded ? undefined : maxLines}>
                      {content.content}
                    </Text>
                    <Group className={styles['contentTable__icon-pill']}>
                      {isTruncated && (
                        <ActionIcon
                          variant="subtle"
                          size="xs"
                          onClick={e => {
                            e.stopPropagation();
                            handleContentClick(index);
                          }}
                        >
                          {isExpanded ? <IconChevronUp size={14} /> : <IconChevronDown size={14} />}
                        </ActionIcon>
                      )}
                      <Tooltip label={t('common.copy')}>
                        <ActionIcon
                          size="xs"
                          variant="subtle"
                          onClick={e => {
                            e.stopPropagation();
                            handleCopyContent(content.content, content.source);
                          }}
                        >
                          <IconCopy size={14} />
                        </ActionIcon>
                      </Tooltip>
                    </Group>
                  </Table.Td>
                )}

                {visibleColumns.includes('source') && (
                  <Table.Td>
                    <Group className={styles['contentTable__source-wrapper']}>
                      <Text size="sm" lineClamp={1}>
                        {extractHostname(content.source)}
                      </Text>
                      <ActionIcon
                        size="sm"
                        variant="light"
                        component="a"
                        href={content.source}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <IconExternalLink size={14} />
                      </ActionIcon>
                    </Group>
                  </Table.Td>
                )}

                {visibleColumns.includes('lang') && (
                  <Table.Td>
                    <Group gap="xs">
                      <span>{getFlagEmoji(content.lang)}</span>
                    </Group>
                  </Table.Td>
                )}

                {visibleColumns.includes('status') && (
                  <Table.Td>{renderStatus(content.status)}</Table.Td>
                )}

                {visibleColumns.includes('actions') && (
                  <Table.Td>
                    <Group justify="flex-end" gap={0}>
                      <Menu withinPortal position="bottom-end" shadow="sm">
                        <Menu.Target>
                          <ActionIcon variant="light">
                            <IconDots size={16} />
                          </ActionIcon>
                        </Menu.Target>
                        <Menu.Dropdown>
                          <Menu.Item
                            leftSection={<IconEye size={14} />}
                            onClick={() => content._id && handlePublishToggle(content)}
                          >
                            {content.status === 'P'
                              ? t('contentTable.unpublish')
                              : t('contentTable.publish')}
                          </Menu.Item>
                          <Menu.Item
                            leftSection={<IconTrash size={14} />}
                            color="red"
                            onClick={() => content._id && handleDelete(content)}
                          >
                            {t('contentTable.delete')}
                          </Menu.Item>
                        </Menu.Dropdown>
                      </Menu>
                    </Group>
                  </Table.Td>
                )}
              </Table.Tr>
            );
          })}
        </Table.Tbody>
      </Table>
    </ScrollArea>
  );
};
